import { UntypedFormBuilder, AbstractControl, UntypedFormArray, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs';
import { ConfigSection } from '../config-section';
import { SecurityGroup, ApprovalGroup } from '@admin/models/system-configuration-model';
import { ApprovalGroupsHttpService } from '@admin/services/approval-groups-http.service';
import { SecurityGroupsHttpService } from '@admin/services/security-groups-http.service';

@Component({
  selector: 'mojo-mojo-approval-group',
  templateUrl: './approval-group.component.html',
  changeDetection: ChangeDetectionStrategy.Default
})
export class ApprovalGroupComponent extends ConfigSection<ApprovalGroup[]> {

  securityGroups: SecurityGroup[];
  approvalGroupNamePostfix: number;

  get formArray(): UntypedFormArray {
    return this.form as UntypedFormArray;
  }

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly approvalGroupService: ApprovalGroupsHttpService,
    private readonly securityGroupService: SecurityGroupsHttpService) {
    super();
  }

  initialiseConfig(): Observable<ApprovalGroup[]> {
    return forkJoin(
      this.approvalGroupService.getApprovalGroups(),
      this.securityGroupService.getSecurityGroups()
    ).pipe(
      switchMap(([approvalGroups, securityGroups]: [ApprovalGroup[], SecurityGroup[]]) => {
        this.securityGroups = securityGroups;
        return of(approvalGroups);
      })
    );
  }

  createForm(approvalGroups: ApprovalGroup[]): void {
    this.form = this.formBuilder.array(
      this.createGroups(approvalGroups),
      this.uniqueGroupNameValidator
    );
  }

  submit(): Observable<boolean> {
    const groups = this.form.getRawValue() as ApprovalGroup[];
    return this.approvalGroupService.saveApprovalGroups(groups).pipe(map(() => true));
  }

  createGroups(groups: ApprovalGroup[]): UntypedFormGroup[] {
    const results: UntypedFormGroup[] = [];
    groups.forEach(g => {
      results.push(this.formBuilder.group(this.approvalGroupToFormGroup(g)));
    });
    return results;
  }

  addNew(): void {
    this.formArray.push(
      this.formBuilder.group(
        this.approvalGroupToFormGroup({
          Name: this.getNextApprovalGroupName(),
          IsDeletionAllowed: true,
          SecurityGroupCodes: null
        }))
    );
  }

  delete(index: number): void {
    this.formArray.removeAt(index);
    this.form.markAsDirty();
  }

  approvalGroupToFormGroup(g: ApprovalGroup): { [key: string]: any } {
    return {
      Uri: new UntypedFormControl(g.Uri),
      Name: new UntypedFormControl(g.Name, { validators: [Validators.required, this.uniqueGroupNameValidator.bind(this)] }),
      IsDeletionAllowed: new UntypedFormControl(g.IsDeletionAllowed),
      SecurityGroupCodes: new UntypedFormControl(g.SecurityGroupCodes, { validators: [Validators.required] })
    };
  }

  getNextApprovalGroupName(): string {
    if (!this.approvalGroupNamePostfix) {
      this.approvalGroupNamePostfix = this.formArray.controls.length;
    }
    this.approvalGroupNamePostfix++;
    return 'Approver Group ' + this.approvalGroupNamePostfix;
  }

  uniqueGroupNameValidator(control: AbstractControl) {
    if (control instanceof UntypedFormArray) {
      const formGroups = control.controls as UntypedFormGroup[];
      const formControls = formGroups.map(x => x.controls['Name']);
      const map = new Map<string, AbstractControl>();
      for (let i = 0; i < formControls.length; i++) {
        const cntrl = formControls[i];
        const key = cntrl.value.toLowerCase();
        if (map.has(key)) {
          const error = { 'duplicate': true };
          map.get(key).setErrors(error);
          cntrl.setErrors(error);
          return error;
        } else if (cntrl.value != '') {
          map.set(key, cntrl);
          cntrl.setErrors(null);
        }
      }
    } else if (control.parent) {
      return this.uniqueGroupNameValidator(control.parent);
    }
    return {};
  }
}
